import React, { useEffect, useState, useRef, useMemo } from 'react';
import { config } from '../../../config';
import './BgAnimate.css';

interface BgAnimateProps {
  /** Animation speed multiplier (1.0 = normal, 2.0 = 2x slower) */
  speed?: number;
  /** Delay between animation cycles in milliseconds */
  delayBetweenCycles?: number;
  /** Whether the animation is enabled */
  enabled?: boolean;
  /** Current game type for theme colors */
  gameType?: string;
}

interface AnimationConfig {
  // Lightning paths
  lightning1Path: { x: number; y: number; rotation: number; width: number }[];
  lightning2Path: { x: number; y: number; rotation: number; width: number }[];
  // Boom container positions
  boom1Position: { left: number; top: number };
  boom2Position: { left: number; top: number };
  // Which shapes to show
  boom1Shapes: string[];
  boom2Shapes: string[];
  // Random directions for boom shapes
  boom1Directions: { translateX: number; translateY: number; rotate: number }[];
  boom2Directions: { translateX: number; translateY: number; rotate: number }[];
  // Theme colors
  primaryColor: string;
  secondaryColor: string;
  accentColor: string;
}

// Generate random lightning path
const generateLightningPath = (width: number, height: number, direction: 'ltr' | 'rtl' | 'ttb' | 'btt' = 'ltr') => {
  const points: { x: number; y: number; rotation: number; width: number }[] = [];
  const numPoints = 6 + Math.floor(Math.random() * 4); // 6-9 points for smoother path drawing
  
  for (let i = 0; i <= numPoints; i++) {
    const progress = i / numPoints;
    let x: number, y: number, rotation: number, pointWidth: number;
    
    // Width varies along the path - make it larger and more visible
    if (progress < 0.15) {
      pointWidth = 16 + (progress / 0.15) * 54; // 16px to 70px
    } else if (progress > 0.85) {
      pointWidth = 70 - ((progress - 0.85) / 0.15) * 54; // 70px back to 16px
    } else {
      pointWidth = 70 + (Math.random() - 0.5) * 80; // 30px to 150px in middle
    }
    
    switch (direction) {
      case 'ltr': // Left to right
        x = progress * width * 0.8 + width * 0.1;
        y = (height / 2) + (Math.random() - 0.5) * height * 0.6;
        rotation = -35 + Math.random() * 70;
        break;
      case 'rtl': // Right to left
        x = (1 - progress) * width * 0.8 + width * 0.1;
        y = (height / 2) + (Math.random() - 0.5) * height * 0.6;
        rotation = 145 + Math.random() * 70;
        break;
      case 'ttb': // Top to bottom
        x = (width / 2) + (Math.random() - 0.5) * width * 0.6;
        y = progress * height * 0.8 + height * 0.1;
        rotation = 25 + Math.random() * 130;
        break;
      case 'btt': // Bottom to top
        x = (width / 2) + (Math.random() - 0.5) * width * 0.6;
        y = (1 - progress) * height * 0.8 + height * 0.1;
        rotation = -155 + Math.random() * 130;
        break;
    }
    
    points.push({ x, y, rotation, width: pointWidth });
  }
  
  return points;
};

// Generate random boom position
const generateBoomPosition = (width: number, height: number) => {
  return {
    left: Math.random() * width * 0.7 + width * 0.15,
    top: Math.random() * height * 0.7 + height * 0.15,
  };
};

// Get random shapes to show - prioritize circles
const getRandomShapes = (count: number = 3): string[] => {
  // Add more circle options
  const allShapes = [
    'circle-big-white', 
    'circle-big-white', // Duplicate to increase chances
    'circle-white', 
    'circle-white', // Duplicate to increase chances
    'triangle-big-yellow', 
    'disc-white', 
    'disc-white', // Duplicate to increase chances
    'triangle-blue'
  ];
  const shuffled = [...allShapes].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, Math.min(count, allShapes.length));
};

// Generate random directions for boom shapes
const generateBoomDirections = (count: number) => {
  return Array.from({ length: count }, () => ({
    translateX: (Math.random() - 0.5) * 200, // -100 to 100px
    translateY: (Math.random() - 0.5) * 200, // -100 to 100px
    rotate: Math.random() * 720, // 0 to 720 degrees
  }));
};

// Get theme colors for game
const getGameColors = (gameType?: string) => {
  const packFallback = {
    primaryColor: config.theme.primaryColor,
    secondaryColor: config.theme.highlightColor,
    accentColor: config.theme.secondaryColor,
  };

  if (!gameType) {
    return packFallback;
  }
  
  const game = config.games.find(g => g.id === gameType);
  if (!game) {
    return packFallback;
  }
  
  // Generate complementary colors from the game color
  const primaryColor = game.color;
  const secondaryColor = adjustColorBrightness(primaryColor, 20);
  const accentColor = adjustColorBrightness(primaryColor, -30);
  
  return { primaryColor, secondaryColor, accentColor };
};

// Adjust color brightness
const adjustColorBrightness = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.min(255, Math.max(0, (num >> 16) + percent));
  const g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + percent));
  const b = Math.min(255, Math.max(0, (num & 0x0000FF) + percent));
  return '#' + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
};

// Generate animation config
const generateAnimationConfig = (gameType?: string): AnimationConfig => {
  // Use viewport dimensions
  const width = typeof window !== 'undefined' ? window.innerWidth : 1920;
  const height = typeof window !== 'undefined' ? window.innerHeight : 1080;
  
  const directions: Array<'ltr' | 'rtl' | 'ttb' | 'btt'> = ['ltr', 'rtl', 'ttb', 'btt'];
  const direction1 = directions[Math.floor(Math.random() * directions.length)];
  const direction2 = directions[Math.floor(Math.random() * directions.length)];
  
  const colors = getGameColors(gameType);
  
  // Increase number of shapes, especially circles
  const boom1ShapeCount = 5 + Math.floor(Math.random() * 4); // 5-8 shapes
  const boom2ShapeCount = 4 + Math.floor(Math.random() * 3); // 4-6 shapes
  
  return {
    lightning1Path: generateLightningPath(width, height, direction1),
    lightning2Path: generateLightningPath(width, height, direction2),
    boom1Position: generateBoomPosition(width, height),
    boom2Position: generateBoomPosition(width, height),
    boom1Shapes: getRandomShapes(boom1ShapeCount),
    boom2Shapes: getRandomShapes(boom2ShapeCount),
    boom1Directions: generateBoomDirections(boom1ShapeCount),
    boom2Directions: generateBoomDirections(boom2ShapeCount),
    ...colors,
  };
};

const BgAnimate: React.FC<BgAnimateProps> = ({ 
  speed = 2.0,
  delayBetweenCycles = 1000, // 1 second between animations
  enabled = true,
  gameType
}) => {
  const [animationKey, setAnimationKey] = useState(0);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Generate new config for each animation cycle
  const animConfig = useMemo(() => generateAnimationConfig(gameType), [animationKey, gameType]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const animationDuration = 3000 * speed;

    const startAnimation = () => {
      setAnimationKey(prev => prev + 1);
      
      timeoutRef.current = setTimeout(() => {
        startAnimation();
      }, animationDuration + delayBetweenCycles);
    };

    startAnimation();

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [speed, delayBetweenCycles, enabled]);

  if (!enabled) {
    return null;
  }

  // Generate dynamic keyframes for lightning with smooth path drawing
  const generateKeyframes = (path: typeof animConfig.lightning1Path) => {
    const keyframes: string[] = [];
    const totalPoints = path.length;
    
    path.forEach((point, i) => {
      const progress = (i / (totalPoints - 1)) * 100;
      keyframes.push(`${progress}% { width: ${point.width}px; transform: translate(${point.x}px, ${point.y}px) rotate(${point.rotation}deg); opacity: 1; }`);
    });
    
    return keyframes.join('\n');
  };

  const lightning1Keyframes = generateKeyframes(animConfig.lightning1Path);
  const lightning2Keyframes = generateKeyframes(animConfig.lightning2Path);

  // Generate dynamic keyframes for boom shapes
  const boom1Keyframes = animConfig.boom1Directions.map((dir, i) => {
    return `@keyframes boom-1-${i}-${animationKey} {
      0% { opacity: 0; }
      5% { opacity: 1; }
      30%, 40% { opacity: 0; transform: scale(${2 + Math.random()}) translate(${dir.translateX}px, ${dir.translateY}px) rotate(${dir.rotate}deg); }
      100% { opacity: 0; }
    }`;
  }).join('\n');

  const boom2Keyframes = animConfig.boom2Directions.map((dir, i) => {
    return `@keyframes boom-2-${i}-${animationKey} {
      0% { opacity: 0; }
      5% { opacity: 1; }
      30%, 40% { opacity: 0; transform: scale(${2 + Math.random()}) translate(${dir.translateX}px, ${dir.translateY}px) rotate(${dir.rotate}deg); }
      100% { opacity: 0; }
    }`;
  }).join('\n');

  return (
    <>
      <style>{`
        @keyframes woosh-1-${animationKey} {
          ${lightning1Keyframes}
        }
        @keyframes woosh-2-${animationKey} {
          ${lightning2Keyframes}
        }
        ${boom1Keyframes}
        ${boom2Keyframes}
      `}</style>
      <div 
        className="bg-animate-container" 
        style={{ 
          '--animation-speed': speed,
          '--primary-color': animConfig.primaryColor,
          '--secondary-color': animConfig.secondaryColor,
          '--accent-color': animConfig.accentColor,
          '--boom-1-left': `${animConfig.boom1Position.left}px`,
          '--boom-1-top': `${animConfig.boom1Position.top}px`,
          '--boom-2-left': `${animConfig.boom2Position.left}px`,
          '--boom-2-top': `${animConfig.boom2Position.top}px`,
        } as React.CSSProperties}
        key={animationKey}
      >
        <div className="lightning-container">
          {/* Primary lightning with trail */}
          <div 
            className="lightning primary trail-0" 
            style={{ animationName: `woosh-1-${animationKey}` }}
          ></div>
          <div 
            className="lightning primary trail-1" 
            style={{ animationName: `woosh-1-${animationKey}` }}
          ></div>
          <div 
            className="lightning primary trail-2" 
            style={{ animationName: `woosh-1-${animationKey}` }}
          ></div>
          <div 
            className="lightning primary trail-3" 
            style={{ animationName: `woosh-1-${animationKey}` }}
          ></div>
          {/* Secondary lightning with trail */}
          <div 
            className="lightning secondary trail-0" 
            style={{ animationName: `woosh-2-${animationKey}` }}
          ></div>
          <div 
            className="lightning secondary trail-1" 
            style={{ animationName: `woosh-2-${animationKey}` }}
          ></div>
          <div 
            className="lightning secondary trail-2" 
            style={{ animationName: `woosh-2-${animationKey}` }}
          ></div>
          <div 
            className="lightning secondary trail-3" 
            style={{ animationName: `woosh-2-${animationKey}` }}
          ></div>
        </div>
        <div className="boom-container boom-1">
          {animConfig.boom1Shapes.map((shapeType, index) => {
            let className = 'shape ';
            if (shapeType === 'circle-big-white') {
              className += 'circle big white';
            } else if (shapeType === 'circle-white') {
              className += 'circle white';
            } else if (shapeType === 'triangle-big-yellow') {
              className += 'triangle big accent';
            } else if (shapeType === 'disc-white') {
              className += 'disc primary';
            } else if (shapeType === 'triangle-blue') {
              className += 'triangle secondary';
            }
            const animationName = `boom-1-${index}-${animationKey}`;
            
            return (
              <div
                key={`${shapeType}-${index}`}
                className={className}
                style={{ animationName }}
              ></div>
            );
          })}
        </div>
        <div className="boom-container boom-2">
          {animConfig.boom2Shapes.map((shapeType, index) => {
            let className = 'shape ';
            if (shapeType === 'circle-big-white') {
              className += 'circle big white';
            } else if (shapeType === 'circle-white') {
              className += 'circle white';
            } else if (shapeType === 'triangle-big-yellow') {
              className += 'triangle big accent';
            } else if (shapeType === 'disc-white') {
              className += 'disc primary';
            } else if (shapeType === 'triangle-blue') {
              className += 'triangle secondary';
            }
            const animationName = `boom-2-${index}-${animationKey}`;
            
            return (
              <div
                key={`${shapeType}-${index}`}
                className={className}
                style={{ animationName }}
              ></div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default BgAnimate;
